import { isNotEmptyObject } from '@impuls-coach/util-is';
import { MeasurementType } from 'components/molecules/Card/Detail/Tracking';
import { getDayByDayNumber } from 'store/programDays';
import { UNIT_MAP } from 'store/programItems';
import {
	ButtonLinkType,
	IconMapType,
	IconMapTypeType,
	ImagesType,
	ImageType,
	LinkType,
	NormalizedExerciseType,
	NormalizedImagesType,
	NormalizedImageType,
	NormalizedRecipe,
	NormalizedVideoType,
	NormalizedWorkoutSet,
	ProgramItemContent,
	ProgramItemsObjectType,
	ProgramItemType,
	RootStateType,
	SelectedContent,
	VideoType,
} from 'store/programItems';
import { BasicLinkDtoType } from 'types/services/magnolia/dtos/BasicLinkDto';
import { ContentTypeRecipeDtoType } from 'types/services/magnolia/dtos/ContentTypeRecipeDto';
import { ProgramItemDtoType } from 'types/services/spring/dtos/ProgramItemDto';

/*
 * Helper function to retrieve content for an item
 */
export const _getItem = (state: RootStateType, itemId: number | string): null | ProgramItemType => {
	if (state.programItems.byId) {
		return state.programItems.byId[itemId];
	}
	return null;
};

/*
 * Helper function to retrieve content for an item
 */
export const _getItemFieldContent = (state: RootStateType, itemId: number | string, field: string): any | null => {
	if (state.programItems.byId) {
		const item = state.programItems.byId[itemId.toString()];

		if (item && item.externalId && Object.keys(state.programItems.contentByExternalId).length !== 0) {
			const itemContent = state.programItems.contentByExternalId[item.externalId];

			if (itemContent && itemContent.selectedContent && itemContent.selectedContent[field]) {
				return itemContent.selectedContent[field];
			}
		}
	}

	return null;
};

/**
 * Returns itemContent of an item
 * @param  { Object } state[description]
 * @param  { string } itemId[description]
 * @throws error if item has no itemContent
 * @return { [type]}[description]
 */
const _getItemContent = (state: RootStateType, itemId?: number | string): ProgramItemContent | null => {
	if (!itemId) {
		return null;
	}
	const item = state.programItems.byId[itemId.toString()];
	if (!item || !item.externalId) {
		return null;
	}
	return state.programItems.contentByExternalId[item.externalId];
};

/**
 * Returns selectedContent of an item.
 * @param  { Object } state[description]
 * @param  { string } itemId[description]
 * @return { [type]}[description]
 */
const _getItemSelectedContent = (state: RootStateType, itemId: number | string): SelectedContent | null => {
	const itemContent = _getItemContent(state, itemId);
	if (itemContent !== null && typeof itemContent === 'object') {
		return itemContent.selectedContent;
	}

	return null;
};

/**
 * Returns array of sorted item IDs:
 * - first: todo items
 * - last: done items
 * @param { Array<number>} itemIds - ids(from day)
 * @param { Object } itemObjects - data from store
 * @return { Array<number>}
 */
const _sortDayItemIdsByIsDone = (
	programItemIds: Array<number>,
	programItems: ProgramItemsObjectType
): Array<number> => {
	return [
		...programItemIds.filter((itemId: number | string) => programItems[itemId].done === false),
		...programItemIds.filter((itemId: number | string) => programItems[itemId].done === true),
	];
};

export const itemTypes = {
	RECIPE: 'recipe',
	SLOGAN: 'slogan',
	TRACKING: 'tracking',
	TIP: 'tip',
	WORKOUT: 'workout',
	TASK: 'task',
	RECOMMENDATION: 'recommendation',
	DYNAMIC_PRODUCT: 'dynamicProduct',
};

export const mapBackendContentType = (contentType: string) => {
	switch (contentType) {
		case 'PRODUCT':
			return itemTypes.RECOMMENDATION;
		case 'RECIPE':
			return itemTypes.RECIPE;
		case 'TIP':
			return itemTypes.TIP;
		case 'TODO':
			return itemTypes.TASK;
		case 'WORKOUT':
			return itemTypes.WORKOUT;
		default:
			return '';
	}
};

export const mapBackendItemType = (type: string) => {
	switch (type) {
		case 'RecipeProgramItem':
			return itemTypes.RECIPE;
		case 'SloganProgramItem':
			return itemTypes.SLOGAN;
		case 'TrackingProgramItem':
			return itemTypes.TRACKING;
		case 'TipProgramItem':
			return itemTypes.TIP;
		case 'WorkoutProgramItem':
			return itemTypes.WORKOUT;
		case 'TodoProgramItem':
			return itemTypes.TASK;
		case 'ProductProgramItem':
			return itemTypes.RECOMMENDATION;
		case 'DynamicProductProgramItem':
			return itemTypes.DYNAMIC_PRODUCT;
		default:
			return '';
	}
};

// Returns an array of program items for today
export const getItems = (state: RootStateType, dayNumber: number | string | null) => {
	let items:
		| {
				id: string;
				type: string;
				isDone: boolean | undefined;
		  }[]
		| null = null;
	const day = getDayByDayNumber(state, dayNumber);
	if (day && Object.keys(state.programItems.byId).length !== 0) {
		const sortedProgramItemIds = _sortDayItemIdsByIsDone(day.programItems, state.programItems.byId);
		const mappedItems = sortedProgramItemIds.map((itemId) => {
			const item = state.programItems.byId[`${itemId}`];
			return {
				id: item.id,
				type: mapBackendItemType(item.type),
				isDone: item.done,
			};
		});

		if (mappedItems) {
			items = mappedItems;
		}
	}
	return items;
};

// Returns if the recipe is from migusto
export const RECIPE_MASTER_CHANNELS = ['migusto'];

export const getIsMigustoRecipe = (state: RootStateType, itemId: number | string): boolean => {
	let isMigustoRecipe = false;

	const itemContent = _getItemContent(state, itemId) as any;
	if (!itemContent) {
		return isMigustoRecipe;
	}
	if (!!itemContent.selectedContent) {
		isMigustoRecipe = RECIPE_MASTER_CHANNELS.includes(itemContent.selectedContent.masterChannel);
	}

	return isMigustoRecipe;
};

// Returns a recipe
export const getRecipeFromSelectedContent = (
	selectedContent: SelectedContent | any,
	optionalVariant?: number
): NormalizedRecipe | any => {
	let content: any = selectedContent;
	const variants: {
		[variant: number]: ContentTypeRecipeDtoType;
	} = selectedContent.variants as any;

	if (optionalVariant) {
		const selectedRecipeVariant = variants[optionalVariant];

		if (selectedRecipeVariant) {
			content = selectedRecipeVariant;
		}
	}

	return {
		baseSize: content.base_size,
		ingredients: content.ingredients,
		duration: selectedContent.duration,
		totalDuration: selectedContent.totalDuration,
		totalDurationFormatted: selectedContent.totalDurationFormatted,
		nutrition: selectedContent.nutrition,
		steps: selectedContent.steps,
		hints: selectedContent.hints,
		nutrients: selectedContent.nutrients,
		contains: selectedContent.contains,
		variants: selectedContent.variants,
	};
};

export const getRecipe = (
	state: RootStateType,
	itemId: number | string,
	optionalVariant?: number
): {} | ReturnType<typeof getRecipeFromSelectedContent> => {
	let recipe = {};

	const itemContent = _getItemContent(state, itemId);

	if (!itemContent) {
		return recipe;
	}

	if (!!itemContent.selectedContent) {
		recipe = getRecipeFromSelectedContent(itemContent.selectedContent, optionalVariant);
	}

	return recipe;
};

export const getRecipeProductsIds = (state: RootStateType, itemId: number | string): '' | string => {
	let recipeProductsIds = '';
	const itemContent = _getItemContent(state, itemId) as any;
	if (!itemContent) {
		return recipeProductsIds;
	}

	if (!!itemContent.selectedContent) {
		recipeProductsIds = itemContent.selectedContent.productsIds;
	}

	return recipeProductsIds;
};

// Returns a string with ids in the format programItemId: selectedContentTypeId
export const getItemContentIdentifiersByDayNumber = (state: RootStateType, dayNumber: number): string => {
	let itemContentIdentifiers = '';
	const currentDay = getDayByDayNumber(state, dayNumber);
	if (currentDay) {
		const identifier = currentDay.programItems
			.filter((itemId) => {
				const item = state.programItems.byId[`${itemId}`];
				if (
					item.type === 'DynamicProductProgramItem' &&
					item.selectedContentType &&
					item.selectedContentType.productId
				) {
					return false;
				}
				return item && item.externalId;
			})
			.map((itemId) => {
				const item = state.programItems.byId[`${itemId}`];

				if (item.externalId && item.selectedContentType && item.selectedContentType.externalId) {
					return `${item.externalId}:${item.selectedContentType.externalId}`;
				}

				return `${item.externalId || ''}`;
			});

		itemContentIdentifiers = identifier.join(',');
	}

	return itemContentIdentifiers;
};

// Returns a string with ids in the format programItemId: selectedContentTypeId
export const getItemContentIdentifierByItemId = (state: RootStateType, itemId: number | string): string => {
	let itemContentIdentifier = '';
	const item = state.programItems.byId[itemId];
	if (item) {
		if (item.externalId) {
			if (item.selectedContentType && item.selectedContentType.externalId) {
				itemContentIdentifier = `${item.externalId}:${item.selectedContentType.externalId}`;
			} else {
				itemContentIdentifier = `${item.externalId}`;
			}
		}
	}

	return itemContentIdentifier;
};

// Returns a boolean if an item is done
export const getIsDone = (state: RootStateType, itemId: number | string): boolean => {
	let itemState = false;

	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];
		if (item && typeof item.done === 'boolean') {
			itemState = item.done;
		}
	}

	return itemState;
};

export const getExternalIdById = (state: RootStateType, itemId: number): string | null | undefined => {
	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];

		if (item) {
			return item.externalId;
		}
	}

	return;
};

// Returns the type of an item
export const getType = (state: RootStateType, itemId: number | string): string => {
	let itemType = '';

	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];
		if (item) {
			itemType = mapBackendItemType(item.type);
		}
	}

	return itemType;
};

export const mapCategoryIcon = (type: IconMapTypeType): string => {
	const iconMap: IconMapType = {
		slogan: 'quote',
		recommendation: 'stars',
		dynamicProduct: 'cart',
	};

	return iconMap[type] ? iconMap[type] : type;
};

// Returns the categoryIcon name of an item
export const getCategoryIcon = (
	state: RootStateType,
	itemId: number | string
): /*FIXME:F2TS*/ ReturnType<typeof mapCategoryIcon> => {
	const type = getType(state, itemId);
	return mapCategoryIcon(type);
};

export const getCardImageFromItemContent = (
	itemContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemContent>
): null | /*FIXME:F2TS*/ SelectedContent['previewImage'] | /*FIXME:F2TS*/ ProgramItemContent['fallbackImage'] => {
	if (itemContent === null || typeof itemContent !== 'object') {
		return null;
	}
	if (itemContent.hasOwnProperty('selectedContent')) {
		const content = itemContent.selectedContent as any;
		const hasSelectedContent = !!itemContent.selectedContent;
		const hasPreviewImage = hasSelectedContent && !!content.previewImage;
		const hasFallbackImage = !!itemContent.fallbackImage;

		if (hasPreviewImage) {
			return content.previewImage;
		} else if (hasFallbackImage) {
			return itemContent.fallbackImage;
		}
	}

	if ((itemContent as any).previewImage) {
		return (itemContent as any).previewImage;
	}

	if (itemContent.fallbackImage) {
		return itemContent.fallbackImage;
	}

	return null;
};

// Returns a card image object.
export const getCardImage = (
	state: RootStateType,
	itemId: number | string
): /*FIXME:F2TS*/ ReturnType<typeof getCardImageFromItemContent> => {
	const itemContent = _getItemContent(state, itemId);
	return getCardImageFromItemContent(itemContent);
};

export const getTitleFromSelectedContent = (selectedContent: SelectedContent | any): string => {
	return selectedContent.title || '';
};
export const getTitle = (state: RootStateType, itemId: number | string): string => {
	let itemTitle = '';

	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];
		if (item && item.externalId && Object.keys(state.programItems.contentByExternalId).length !== 0) {
			const itemContent = state.programItems.contentByExternalId[item.externalId];
			if (itemContent && itemContent.selectedContent) {
				itemTitle = getTitleFromSelectedContent(itemContent.selectedContent);
			}
		}
	}

	return itemTitle;
};

export const getMenuTypeLabel = (state: RootStateType, itemId: number | string) => {
	let label: string | undefined;

	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];
		if (item && item.externalId && Object.keys(state.programItems.contentByExternalId).length !== 0) {
			const itemContent = state.programItems.contentByExternalId[item.externalId];
			if (itemContent && itemContent.menuTypeLabel) {
				label = itemContent.menuTypeLabel;
			}
		}
	}

	return label;
};

export const getMenuTypeFilterTagId = (state: RootStateType, itemId: number | string) => {
	let label: string | undefined;

	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];
		if (item && item.externalId && Object.keys(state.programItems.contentByExternalId).length !== 0) {
			const itemContent = state.programItems.contentByExternalId[item.externalId];
			if (itemContent && itemContent.menuTypeFilterTagId) {
				label = itemContent.menuTypeFilterTagId;
			}
		}
	}

	return label;
};

export const getRecipeCalories = (state: RootStateType, itemId: number | string): string => {
	let label: string = '0';

	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];
		if (item && item.externalId && Object.keys(state.programItems.contentByExternalId).length !== 0) {
			const itemContent = state.programItems.contentByExternalId[item.externalId];
			if (itemContent && (itemContent.selectedContent as ContentTypeRecipeDtoType).calories) {
				label = (itemContent.selectedContent as ContentTypeRecipeDtoType).calories;
			}
		}
	}

	return label;
};

export const getNutrition = (state: RootStateType, itemId: number | string): string => {
	let label: string = '';

	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];
		if (item && item.externalId && Object.keys(state.programItems.contentByExternalId).length !== 0) {
			const itemContent = state.programItems.contentByExternalId[item.externalId];
			if (itemContent && (itemContent.selectedContent as ContentTypeRecipeDtoType).nutrition) {
				label = (itemContent.selectedContent as ContentTypeRecipeDtoType).nutrition;
			}
		}
	}

	return label;
};

export const getNutritionFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent | any>
): string => {
	let nutrition = '';

	if (selectedContent) {
		nutrition = selectedContent.nutrition || '';
	}

	return nutrition;
};

// Returns the productId of a program item or an empty string
export const getProductId = (state: RootStateType, itemId: number | string): string => {
	const itemContent = _getItemContent(state, itemId);
	let productId = '';

	if (itemContent) {
		productId = itemContent.productId;
	}

	return productId;
};

export const getDisableRetailerContext = (state: RootStateType, itemId: number | string): boolean => {
	const itemContent = _getItemContent(state, itemId);
	return itemContent ? !!itemContent.disableRetailerContext : false;
};

// Returns the valid range of a program item or an empty string
export const getValidRange = (
	state: RootStateType,
	itemId: number
): {
	from: string | /*FIXME:F2TS*/ ProgramItemContent['validFrom'];
	to: string | /*FIXME:F2TS*/ ProgramItemContent['validTo'];
} => {
	const itemContent = _getItemContent(state, itemId);
	let validRange = {
		from: '',
		to: '',
	};

	if (itemContent) {
		validRange = {
			from: itemContent.validFrom as any,
			to: itemContent.validTo as any,
		};
	}

	return validRange;
};

// Returns the logo of a program item
export const getLogo = (state: RootStateType, itemId: number | string): /*FIXME:F2TS*/ SelectedContent['logo'] | {} => {
	const itemContent = _getItemContent(state, itemId) as any;
	let logo = {};

	if (itemContent && itemContent.selectedContent && itemContent.selectedContent.logo) {
		logo = itemContent.selectedContent.logo;
	}

	return logo;
};

export const getThirdPartyLogo = (
	state: RootStateType,
	itemId: number
): /*FIXME:F2TS*/ SelectedContent['thirdPartyLogo'] | {} => {
	const itemContent = _getItemContent(state, itemId) as any;
	let thirdPartyLogo = {};

	if (itemContent && itemContent.selectedContent && itemContent.selectedContent.thirdPartyLogo) {
		thirdPartyLogo = itemContent.selectedContent.thirdPartyLogo;
	}

	return thirdPartyLogo;
};

export const getInfoText = (state: RootStateType, itemId: number | string): string => {
	const itemContent = _getItemContent(state, itemId);
	let itemInfoText = '';

	if (itemContent && itemContent.selectedContent) {
		itemInfoText = (itemContent.selectedContent as any).infoText;
	}

	return itemInfoText;
};

// Returns the slogan of a program item or ""
export const getSlogan = (state: RootStateType, itemId: number): string => {
	let slogan = '';

	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];
		if (item && item.selectedContentType && item.selectedContentType.slogan) {
			slogan = item.selectedContentType.slogan;
		}
	}

	if (slogan === '') {
		const itemContent = _getItemContent(state, itemId);
		if (itemContent && typeof itemContent === 'object') {
			slogan = itemContent.slogans[0];
		}
	}

	return slogan;
};

export const getKcalFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent | any>
): string => {
	let energy = '';

	if (selectedContent) {
		energy = `${(selectedContent.nutrients || {}).calories} kcal`;
	}

	return energy;
};

export const getKcal = (state: RootStateType, itemId: number | string): string => {
	return getKcalFromSelectedContent(_getItemSelectedContent(state, itemId));
};

export const getWorkoutCaloriesFromSelectedContent = (selectedContent: SelectedContent | any): number => {
	return selectedContent.calories || 0;
};
export const getWorkoutCalories = (state: RootStateType, itemId: number | string): number => {
	let calories = 0;

	const selectedContent = _getItemSelectedContent(state, itemId);

	if (selectedContent) {
		calories = getWorkoutCaloriesFromSelectedContent(selectedContent);
	}

	return calories;
};

export const getWorkoutTotalDurationFromSelectedContent = (selectedContent: SelectedContent | any): number => {
	const duratioInSeconds = selectedContent.duration || 0;
	return Math.round(duratioInSeconds / 60);
};
export const getWorkoutTotalDuration = (state: RootStateType, itemId: number | string): number => {
	let totalDuration = -1;

	const selectedContent = _getItemSelectedContent(state, itemId);
	if (selectedContent) {
		totalDuration = getWorkoutTotalDurationFromSelectedContent(selectedContent);
	}

	return totalDuration;
};

export const getTotalDurationFormattedFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent | any>
): string => {
	let totalDurationFormatted = '';

	if (selectedContent) {
		totalDurationFormatted = selectedContent.totalDurationFormatted || '';
	}

	return `${totalDurationFormatted}`;
};

export const getTotalDurationFormatted = (state: RootStateType, itemId: number | string): string => {
	return getTotalDurationFormattedFromSelectedContent(_getItemSelectedContent(state, itemId));
};

export const getTotalDurationFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent | any>
): string => {
	let totalDuration = '';

	if (selectedContent) {
		totalDuration = selectedContent.totalDuration || '';
	}

	return `${totalDuration}`;
};

export const getTotalDuration = (state: RootStateType, itemId: number | string): string => {
	return getTotalDurationFromSelectedContent(_getItemSelectedContent(state, itemId));
};

export const getIntensityFromSelectedContent = (selectedContent: SelectedContent | any): number => {
	return parseInt(`${selectedContent.intensity}`, 10);
};
/**
 * Returns intensity of an item or empty string if nit defined
 * @param  {Object} state  [description]
 * @param  {string} itemId [description]
 * @return {number}        [description]
 */
export const getIntensity = (state: RootStateType, itemId: number | string): number => {
	let intensity = -1;

	const selectedContent = _getItemSelectedContent(state, itemId);
	if (selectedContent) {
		intensity = getIntensityFromSelectedContent(selectedContent);
	}

	return parseInt(`${intensity}`, 10);
};

export const getDescriptionFromSelectedContent = (selectedContent: SelectedContent | any): string => {
	return selectedContent.description || '';
};
/**
 * Returns description of an item or empty string if nit defined
 * @param  {Object} state
 * @param  {string} itemId
 * @return {string}
 */
export const getDescription = (state: RootStateType, itemId: number | string): string => {
	let value = '';

	const selectedContent = _getItemSelectedContent(state, itemId);
	if (selectedContent) {
		value = getDescriptionFromSelectedContent(selectedContent);
	}

	return value;
};

export const getDescriptionShortFromSelectedContent = (selectedContent: SelectedContent | any): string => {
	return selectedContent.descriptionShort || '';
};
/**
 * Returns descriptionShort of an item or empty string if not defined
 * @param  {Object} state
 * @param  {string} itemId
 * @return {string}
 */
export const getDescriptionShort = (state: RootStateType, itemId: number | string): string => {
	let value = '';

	const selectedContent = _getItemSelectedContent(state, itemId);
	if (selectedContent) {
		value = getDescriptionShortFromSelectedContent(selectedContent);
	}

	return value;
};

export const getToolListFromSelectedContent = (selectedContent: SelectedContent | any): any => {
	return (selectedContent || {}).tools || [];
};
/**
 * Returns list of tools or empty list if not defined
 * @param  {Object} state
 * @param  {string} itemId
 * @return {array}
 */
export const getToolList = (
	state: RootStateType,
	itemId: number | string
): /*FIXME:F2TS*/ ReturnType<typeof getToolListFromSelectedContent> => {
	let list: BasicLinkDtoType[] = [];

	const selectedContent = _getItemSelectedContent(state, itemId);
	if (selectedContent) {
		list = getToolListFromSelectedContent(selectedContent);
	}

	return list;
};

export const getItemRichTextFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent>
): string => {
	return (selectedContent || ({} as any)).text;
};
/**
 * Returns the title of a program item or an empty string
 */
export const getItemRichText = (state: RootStateType, itemId: number | string): any => {
	return _getItemFieldContent(state, itemId, 'text');
};

export const getItemContentVideoFromField = (videoField: VideoType | null): NormalizedVideoType | null => {
	if (videoField !== null) {
		return {
			video: {
				code: videoField.embedCode,
			},
			caption: {
				title: videoField.title,
				text: videoField.text,
			},
		};
	}

	return null;
};
export const getItemContentVideoFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent>
): /*FIXME:F2TS*/ ReturnType<typeof getItemContentVideoFromField> => {
	const videoField: VideoType | null = (selectedContent || ({} as any)).video || null;
	return getItemContentVideoFromField(videoField);
};
export const getItemContentVideo = (state: RootStateType, itemId: number | string): NormalizedVideoType | null => {
	const videoField: VideoType | null = _getItemFieldContent(state, itemId, 'video');
	return getItemContentVideoFromField(videoField);
};

export const getImagesFromField = (field: ImagesType | null): NormalizedImageType[] | null => {
	let itemImages: NormalizedImageType[] | null = null;

	if (field !== null && field.length > 0) {
		itemImages = field.map(
			(image: ImageType): NormalizedImageType => {
				const lightboxSrcset = image.lightboxSrcset || image.srcset;
				return {
					image: {
						srcset: image.srcset,
						alt: image.alt,
						title: image.title,
					},
					caption: {
						title: image.title,
						text: image.text,
					},
					lightbox: {
						image: {
							srcset: lightboxSrcset,
							alt: image.alt,
							title: image.title,
						},
					},
				};
			}
		);
	}

	return itemImages;
};
export const getImagesFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent>
): /*FIXME:F2TS*/ ReturnType<typeof getImagesFromField> => {
	const imagesField: ImagesType | null = (selectedContent || ({} as any)).images || null;
	return getImagesFromField(imagesField);
};
export const getImages = (state: RootStateType, itemId: number | string): NormalizedImagesType | null => {
	const imagesField: ImagesType | null = _getItemFieldContent(state, itemId, 'images');
	return getImagesFromField(imagesField);
};

export const getTextFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent>
): string | null => {
	return (selectedContent || ({} as any)).text || null;
};
export const getText = (state: RootStateType, itemId: number | string): string | null => {
	return _getItemFieldContent(state, itemId, 'text') || null;
};

export const getLinkFromField = (linkField: LinkType | null): LinkType | null => {
	if (linkField && typeof linkField === 'object') {
		return {
			title: linkField.title,
			href: linkField.href,
			openInNewWindow: linkField.openInNewWindow,
		};
	}

	return null;
};
export const getLinkFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent>
): LinkType | null => {
	const linkField: LinkType | null = (selectedContent || ({} as any)).link || null;
	return getLinkFromField(linkField);
};
export const getLink = (state: RootStateType, itemId: number | string): LinkType | null => {
	const linkField: LinkType | null = _getItemFieldContent(state, itemId, 'link');
	return getLinkFromField(linkField);
};

export const getButtonLinkFromField = (linkField: LinkType | null): ButtonLinkType | null => {
	if (linkField !== null && typeof linkField === 'object') {
		return {
			modifier: 'button',
			title: linkField.title,
			href: linkField.href,
			openInNewWindow: linkField.openInNewWindow,
		};
	}

	return null;
};
export const getButtonLink = (state: RootStateType, itemId: number | string): ButtonLinkType | null => {
	const linkField: LinkType | null = _getItemFieldContent(state, itemId, 'link');
	return getButtonLinkFromField(linkField);
};
export const getButtonLinkFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent>
): ButtonLinkType | null => {
	const linkField: LinkType | null = (selectedContent || ({} as any)).link || null;
	return getButtonLinkFromField(linkField);
};

export const getHasButtonLinkFromSelectedContent = (
	selectedContent: /*FIXME:F2TS*/ ReturnType<typeof _getItemSelectedContent>
): boolean => {
	return isNotEmptyObject(getButtonLinkFromSelectedContent(selectedContent));
};
export const getHasButtonLink = (state: RootStateType, itemId: number | string): boolean => {
	return isNotEmptyObject(getButtonLink(state, itemId));
};

// Returns measurement tracking info
export const getMeasurement = (state: RootStateType, itemId: number | string): MeasurementType | null => {
	let selectedContentType: /*FIXME:F2TS*/ ProgramItemDtoType['selectedContentType'] | null = null;
	let measurement: Partial<MeasurementType> | null = null;

	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];
		if (item && item.selectedContentType) {
			selectedContentType = item.selectedContentType;
		}
	}

	if (selectedContentType && selectedContentType.date) {
		measurement = {
			weight: selectedContentType.weight,
			waist: selectedContentType.waist,
			bodyFat: selectedContentType.bodyFat,
			time: selectedContentType.date.time,
			date: selectedContentType.date.date,
		};
	}

	return measurement as any;
};

export const getWorkoutSetsFromSelectedContent = (
	selectedContent: SelectedContent | any
): Array<NormalizedWorkoutSet> | null => {
	return (selectedContent || ({} as any)).workoutSets.workoutSets.map((workoutSet, index: number): any => {
		return {
			id: index,
			href: '/index', // FIXME: wtf?
			cells: [
				{
					image: workoutSet.exercise.previewImage ? workoutSet.exercise.previewImage : null,
				},
				{
					text: `${workoutSet.amount} ${UNIT_MAP[workoutSet.exercise.unit]}`,
				},
				{
					text: workoutSet.exercise.title,
				},
			],
		};
	});
};

// Returns the workout sets
export const getWorkoutSets = (state: RootStateType, itemId: number | string): Array<NormalizedWorkoutSet> | null => {
	const selectedContent = _getItemSelectedContent(state, itemId);
	let workoutSets: NormalizedWorkoutSet[] | null = null;

	if (selectedContent) {
		workoutSets = getWorkoutSetsFromSelectedContent(selectedContent);
	}

	return workoutSets;
};

export const getExercisesFromSelectedContent = (
	selectedContent: SelectedContent | any
): NormalizedExerciseType[] | null => {
	return (selectedContent || {}).workoutSets.workoutSets.map((workoutSet) => {
		let itemImages: any = null;
		let itemVideo: any = null;
		if (workoutSet.exercise.images) {
			itemImages = workoutSet.exercise.images.map((image) => {
				const lightboxSrcset = image.lightboxSrcset || image.srcset;
				return {
					image: {
						srcset: image.srcset,
						alt: image.alt,
						title: image.title,
					},
					caption: {
						title: image.title,
						text: image.text,
					},
					lightbox: {
						image: {
							srcset: lightboxSrcset,
							alt: image.alt,
							title: image.title,
						},
					},
				};
			});
		}
		if (workoutSet.exercise.video) {
			itemVideo = {
				video: {
					code: workoutSet.exercise.video.embedCode,
				},
				caption: {
					title: workoutSet.exercise.video.title,
					text: workoutSet.exercise.video.text,
				},
			};
		}

		return {
			title: `${workoutSet.amount} ${UNIT_MAP[workoutSet.exercise.unit]} ${workoutSet.exercise.title}`,
			amount: workoutSet.amount,
			bodyRegions: workoutSet.exercise.bodyRegions,
			images: itemImages,
			contentVideo: itemVideo,
			unit: workoutSet.exercise.unit,
		};
	});
};
/**
 * Returns the workout sets
 */
export const getExercises = (state: RootStateType, itemId: number | string): NormalizedExerciseType[] | null => {
	const selectedContent = _getItemSelectedContent(state, itemId);
	let exercises: NormalizedExerciseType[] | null = null;

	if (selectedContent) {
		exercises = getExercisesFromSelectedContent(selectedContent);
	}
	return exercises;
};

export const getRepetitionsPerWorkoutSetFromSelectedContent = (selectedContent: SelectedContent | any): number => {
	return parseInt(`${(selectedContent.workoutSets || ({} as any)).repetitions || -1}`, 10);
};
/**
 * Returns the numberOfRepetitionsPerWorkoutSet
 */
export const getRepetitionsPerWorkoutSet = (state: RootStateType, itemId: number | string): number => {
	let numberOfRepetitionsPerWorkoutSet = -1;

	const selectedContent = _getItemSelectedContent(state, itemId) as any;
	if (selectedContent && selectedContent.workoutSets) {
		numberOfRepetitionsPerWorkoutSet = getRepetitionsPerWorkoutSetFromSelectedContent(selectedContent);
	}

	return numberOfRepetitionsPerWorkoutSet;
};

export const getPausePerWorkoutSetFromSelectedContent = (selectedContent: SelectedContent | any): number => {
	return parseInt(`${(selectedContent.workoutSets || ({} as any)).pausePerExercise || -1}`, 10);
};

// Returns the pause on workout sets
export const getPausePerWorkoutSet = (state: RootStateType, itemId: number | string): number => {
	let pause = -1;

	const selectedContent = _getItemSelectedContent(state, itemId) as any;
	if (selectedContent && selectedContent.workoutSets) {
		pause = getPausePerWorkoutSetFromSelectedContent(selectedContent);
	}

	return pause;
};

export const getIsUpdatingTracking = (state: RootStateType): boolean => {
	return Boolean(state.programItems.isUpdatingTracking);
};

export const getIsFavorite = (state: RootStateType, itemId: number | string): boolean => {
	let isFavorite = false;

	if (Object.keys(state.programItems.byId).length !== 0) {
		const item = state.programItems.byId[itemId];

		if (item && typeof item.favorite === 'boolean') {
			isFavorite = item.favorite;
		}
	}

	return isFavorite;
};

export const getExternalId = (state: RootStateType, itemId: number | string): string | null => {
	const selectedContent = _getItemSelectedContent(state, itemId) as any;

	if (selectedContent && selectedContent.externalId) {
		return selectedContent.externalId;
	}

	return null;
};

export const getProgramId = (state: RootStateType, itemId: number | string): string | null => {
	return (_getItemContent(state, itemId) || ({} as any)).productId;
};

export const getIsCustomItem = (state: RootStateType, itemId: number | string): boolean => {
	const programItem: any = _getItem(state, itemId) || {};
	const hasIsCustomFlag = programItem.isCustom !== undefined && programItem.isCustom === true;
	const hasCustomSelectedContent: boolean = Boolean(
		programItem.selectedContentType && programItem.selectedContentType.type === 'CustomWorkoutContentType'
	);

	return hasIsCustomFlag || hasCustomSelectedContent;
};

export const getIsPostponed = (state: RootStateType, itemId: number | string): boolean => {
	const programItem = _getItem(state, itemId);

	if (programItem) {
		return programItem.isPostponed;
	}

	return false;
};

export const getSelectedContentType = (
	state: RootStateType,
	itemId: number | string
): /*FIXME:F2TS*/ ProgramItemDtoType['selectedContentType'] => {
	return state.programItems.byId[itemId].selectedContentType;
};
export const getSelectedExerciseId = (state: RootStateType, itemId: number | string): string | null => {
	const isCustomItem = getIsCustomItem(state, itemId);

	if (!isCustomItem) {
		return null;
	}
	return (getSelectedContentType(state, itemId) || ({} as any)).externalId;
};
export const getSelectedDuration = (state: RootStateType, itemId: number | string): number | null => {
	const isCustomItem = getIsCustomItem(state, itemId);

	if (!isCustomItem) {
		return null;
	}

	return (getSelectedContentType(state, itemId) || ({} as any)).duration;
};
export const getSelectedCalories = (state: RootStateType, itemId: number | string): number | null => {
	const isCustomItem = getIsCustomItem(state, itemId);

	if (!isCustomItem) {
		return null;
	}
	return (getSelectedContentType(state, itemId) || ({} as any)).calories;
};
